import React from 'react';
import {
  FileText,
  CheckCircle,
  Circle,
} from 'lucide-react';
import { KYC_SECTION_ICON_MAP } from '../../../../types/kyc.constants';

interface SidebarSection {
  id: string;
  title: string;
  icon: string;
}

interface KYCSidebarProps {
  sections: SidebarSection[];
  activeSection: string;
  onSectionClick: (id: string) => void;
  completionStatus: Record<string, boolean>;
}

const iconMap = KYC_SECTION_ICON_MAP;

const KYCSidebar: React.FC<KYCSidebarProps> = ({
  sections,
  activeSection,
  onSectionClick,
  completionStatus,
}) => {
  return (
    <aside className="w-64 sticky top-4 h-fit hidden md:block">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h3 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">
          KYC Sections
        </h3>
        <nav className="space-y-1">
          {sections.map((section) => {
            const isActive = activeSection === section.id;
            const isComplete = completionStatus[section.id] === true;
            const IconComponent = iconMap[section.icon] || FileText;

            return (
              <button
                key={section.id}
                onClick={() => onSectionClick(section.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all
                  ${
                    isActive
                      ? 'bg-kielis-gold text-kielis-navy font-medium shadow-sm'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                aria-label={`Navigate to ${section.title}`}
                aria-current={isActive ? 'location' : undefined}
              >
                <span className={isActive ? 'text-kielis-navy' : 'text-gray-500'}>
                  <IconComponent className="w-4 h-4" />
                </span>
                <span className="flex-1 text-left truncate">{section.title}</span>
                {isComplete ? (
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" aria-label="Complete" />
                ) : (
                  <Circle className="w-4 h-4 text-gray-300 flex-shrink-0" aria-label="Incomplete" />
                )}
              </button>
            );
          })}
        </nav>

        {/* Completion Summary */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="text-xs text-gray-600">
            {Object.values(completionStatus).filter(Boolean).length} of {sections.length} sections complete
          </div>
        </div>
      </div>
    </aside>
  );
};

export default KYCSidebar;
